import type { AppPlan } from './planTypes';

/**
 * What to draw inside each phone frame on the look step.
 *
 * The step shows three kits wearing the person's own app, and the reason it works — the
 * reason it produces anticipation rather than a shrug — is that they recognise it. So every
 * word in a frame comes out of the plan: the screens the planner named, the fields the owner
 * will type in, the thing a visitor sends. A frame carrying lorem, or a field this app will
 * not have, is a picture of a different app; it would make the most persuasive step in the
 * wizard the one least connected to what gets built.
 *
 * Pure and tested here rather than inside the component, because the wizard has no component
 * tests and this is the half that can be wrong in a way nobody notices.
 */

export type MockupKind = 'list' | 'detail' | 'form' | 'confirm';

export interface MockupScreen {
  id: string;
  title: string;
  kind: MockupKind;
  /** Short labels drawn as rows. Real field or feature names, never filler. */
  rows: string[];
  /** The accent button, when this frame has one to show. */
  action?: string;
}

const MAX_FRAMES = 3;
const MAX_ROWS = 3;

/** Shortened to what fits a 96pt frame without becoming an ellipsis. */
const fit = (s: string) => (s.length <= 18 ? s : `${s.slice(0, 17)}…`);

/** A screen the plan itself describes as the end of the journey. */
const CONFIRMING = /\b(confirm|confirmed|thank|thanks|done|success|receipt)\b/i;

/**
 * What a frame is showing, read off the plan rather than assumed from position.
 *
 * Position alone put a "Confirmed" frame last in every collecting app, which is the least
 * informative screen there is — a filmstrip that ends on a tick shows nothing about the app.
 * A three-frame strip of a collecting app should end on the FORM, because that is the moment
 * the visitor does the thing the app exists for.
 */
function kindFor(screen: { label: string; purpose: string }, index: number, collects: boolean): MockupKind {
  if (CONFIRMING.test(`${screen.label} ${screen.purpose}`)) return 'confirm';
  if (index === 0) return 'list';
  if (collects && index >= 2) return 'form';
  return 'detail';
}

export function mockupScreens(plan: AppPlan): MockupScreen[] {
  const collects = plan.collects != null && plan.collects.fields.length > 0;
  const managedFields = plan.manages.flatMap((m) => m.fields);
  const featureLabels = plan.features.filter((f) => f.included).map((f) => f.label);

  // The screens the planner named. An empty list still has to draw something, because the
  // step paints on the first frame — before any tailoring call has answered.
  const screens = plan.screens.length
    ? plan.screens.slice(0, MAX_FRAMES)
    : [{ id: 'overview', label: plan.appName, purpose: '' }];

  return screens.map((screen, i) => {
    const kind = kindFor(screen, i, collects);

    /*
     * Row source, best first. Managed fields are what the OWNER will type, collected fields
     * are what a VISITOR sends — showing the right one for the frame is what makes a person
     * recognise their app rather than a generic one. Feature labels are the honest fallback:
     * still theirs, still from the plan, just less specific.
     */
    const source =
      kind === 'form' && collects
        ? plan.collects!.fields
        : managedFields.length
          ? managedFields
          : featureLabels;

    const rows = (source.length ? source : [screen.label]).slice(0, MAX_ROWS).map(fit);

    const action =
      kind === 'form'
        ? 'Send'
        : kind === 'detail' && collects
          ? fit(featureLabels[2] ?? screens[i + 1]?.label ?? 'Continue')
          : undefined;

    return { id: screen.id, title: screen.label, kind, rows, ...(action ? { action } : {}) };
  });
}
